/**
 * Keyboard shortcuts. Combos: "c", "mod+k" (Cmd on Mac, Ctrl elsewhere),
 * "shift+d", and two-key sequences like "g i". Shortcuts don't fire while
 * typing in a field unless `inInputs` is set.
 */
import { useEffect, useRef } from "react";

export const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);

interface Binding {
  combo: string[];
  handler: (e: KeyboardEvent) => void;
  inInputs: boolean;
}

const bindings = new Set<Binding>();
let lastKey: { key: string; at: number } | null = null;

function isTyping(e: KeyboardEvent) {
  const t = e.target as HTMLElement | null;
  if (!t) return false;
  return t.isContentEditable || t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.tagName === "SELECT" || !!t.closest("[cmdk-root]");
}

function keyOf(e: KeyboardEvent): string {
  const parts: string[] = [];
  if (isMac ? e.metaKey : e.ctrlKey) parts.push("mod");
  if (e.altKey) parts.push("alt");
  if (e.shiftKey && e.key.length > 1) parts.push("shift");
  if (e.shiftKey && e.key.length === 1 && /[a-z]/i.test(e.key)) parts.push("shift");
  const k = e.key === " " ? "space" : e.key.length === 1 ? e.key.toLowerCase() : e.key.toLowerCase();
  parts.push(k);
  return parts.join("+");
}

if (typeof window !== "undefined") {
  window.addEventListener("keydown", (e) => {
    if (e.defaultPrevented || e.isComposing) return;
    const key = keyOf(e);
    const typing = isTyping(e);
    const prev = lastKey && Date.now() - lastKey.at < 1000 ? lastKey.key : null;
    // Two-key sequences ("g i") beat single keys; among equals, the most
    // recently registered binding wins (pages override globals).
    const list = [...bindings].reverse();
    for (const pass of ["sequence", "single"] as const) {
      for (const b of list) {
        if (typing && !b.inInputs) continue;
        const hit = b.combo.some((c) => {
          const seq = c.split(" ");
          if (seq.length === 2) return pass === "sequence" && prev === seq[0] && key === seq[1];
          return pass === "single" && c === key;
        });
        if (hit) {
          e.preventDefault();
          lastKey = null;
          b.handler(e);
          return;
        }
      }
    }
    if (!typing) lastKey = { key, at: Date.now() };
  });
}

export function useHotkey(combo: string | string[], handler: (e: KeyboardEvent) => void, opts: { enabled?: boolean; inInputs?: boolean } = {}) {
  const ref = useRef(handler);
  ref.current = handler;
  const combos = Array.isArray(combo) ? combo : [combo];
  const key = combos.join("|");
  const enabled = opts.enabled ?? true;
  useEffect(() => {
    if (!enabled) return;
    const b: Binding = { combo: key.split("|"), handler: (e) => ref.current(e), inInputs: opts.inInputs ?? false };
    bindings.add(b);
    return () => {
      bindings.delete(b);
    };
  }, [key, enabled, opts.inInputs]);
}

/** "⌘K" / "Ctrl K" for display. */
export function shortcutLabel(combo: string): string {
  return combo
    .split("+")
    .map((p) => (p === "mod" ? (isMac ? "⌘" : "Ctrl") : p === "shift" ? "⇧" : p === "alt" ? (isMac ? "⌥" : "Alt") : p.length === 1 ? p.toUpperCase() : p))
    .join(isMac ? "" : " ");
}
